import { useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchAllAirtableRecords,
  createAirtableRecord,
  updateAirtableRecord,
  deleteAirtableRecord
} from '../../services/airtable';

const DEFAULT_RADIUS = 120;
const SEARCH_MIN_CHARS = 5;
const SEARCH_DEBOUNCE_MS = 380;
const SEARCH_LIMIT = 6;
const SEARCH_VARIANTS_LIMIT = 4;
const LOCATION_FIELD_FALLBACK_NAMES = {
  name: 'Nombre',
  lat: 'Latitud',
  lng: 'Longitud',
  radius: 'Radio',
  active: 'Activo',
  notes: 'Notas'
};

const toText = value => {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) {
    const first = value.find(item => item !== undefined && item !== null && String(item).trim());
    return first === undefined ? '' : toText(first);
  }
  return String(value).trim();
};

const toNumber = value => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toBoolean = value => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  const text = toText(value).toLowerCase();
  return ['true', '1', 'si', 'sí', 'activo', 'activa', 'yes'].includes(text);
};

const isAirtableFieldId = value => /^fld[a-zA-Z0-9]{14}$/.test(toText(value));

const getFieldValue = (fields, configuredKey, fallbackName) => {
  if (!fields || typeof fields !== 'object') return undefined;
  if (configuredKey && Object.prototype.hasOwnProperty.call(fields, configuredKey)) {
    return fields[configuredKey];
  }
  if (fallbackName && Object.prototype.hasOwnProperty.call(fields, fallbackName)) {
    return fields[fallbackName];
  }
  return undefined;
};

const GEO_SEARCH_ENDPOINT = toText(process.env.REACT_APP_GEO_SEARCH_URL);
const GEO_SEARCH_ALLOW_NOMINATIM = !['false', '0', 'no'].includes(
  toText(process.env.REACT_APP_GEO_SEARCH_ALLOW_NOMINATIM).toLowerCase()
);

const joinCompact = (parts, separator = ', ') =>
  parts
    .map(part => toText(part))
    .filter(Boolean)
    .join(separator);

const pickText = (...values) => {
  for (const value of values) {
    const text = toText(value);
    if (text) return text;
  }
  return '';
};

const toFixedCoordinate = value => {
  const numeric = toNumber(value);
  return numeric === null ? '' : numeric.toFixed(6);
};

const normalizeSpace = value => toText(value).replace(/\s+/g, ' ').trim();

const sanitizeSearchText = rawQuery => {
  let text = normalizeSpace(rawQuery);
  if (!text) return '';

  text = text
    .replace(/https?:\/\/\S+/gi, ' ')
    .replace(/\bwww\.\S+/gi, ' ')
    .replace(/\bmaps\.app\.goo\.gl\/\S+/gi, ' ')
    .replace(/\bgoogle\.com\/maps\/\S+/gi, ' ')
    .replace(/\bC\.\s+/gi, 'Calle ')
    .replace(/\bAv\.\s+/gi, 'Avenida ')
    .replace(/\bBlvd\.\s+/gi, 'Boulevard ')
    .replace(/\bCd\.\s+/gi, 'Ciudad ')
    .replace(/\bChih\.\b/gi, 'Chihuahua')
    .replace(/\bMéx\.\b/gi, 'México')
    .replace(/\bAC\s+Negocios\b/gi, ' ')
    .replace(/\s{2,}/g, ' ');

  return normalizeSpace(text);
};

const pushVariant = (variants, value) => {
  const clean = normalizeSpace(value);
  if (!clean) return;
  const key = clean.toLowerCase();
  if (variants.some(item => item.toLowerCase() === key)) return;
  variants.push(clean);
};

const buildSearchVariants = rawQuery => {
  const cleanQuery = sanitizeSearchText(rawQuery);
  if (!cleanQuery) return [];

  const variants = [];
  pushVariant(variants, cleanQuery);

  const segments = cleanQuery
    .split(',')
    .map(segment => normalizeSpace(segment))
    .filter(Boolean);

  if (segments.length > 1) {
    pushVariant(variants, segments.slice(0, 3).join(', '));
    pushVariant(variants, `${segments[0]}, ${segments[segments.length - 1]}`);
  }

  const broadWithoutHouse = normalizeSpace(cleanQuery.replace(/\b\d+[A-Za-z-]*\b/g, ' '));
  if (broadWithoutHouse && broadWithoutHouse !== cleanQuery) {
    pushVariant(variants, broadWithoutHouse);
  }

  const hasChihuahua = /chihuahua/i.test(cleanQuery);
  if (!hasChihuahua && segments.length) {
    pushVariant(variants, `${segments[0]}, Chihuahua, Chihuahua, México`);
  }

  return variants.slice(0, SEARCH_VARIANTS_LIMIT);
};

const decodeURIComponentSafe = value => {
  const text = toText(value);
  if (!text) return '';
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
};

const toLatLngPair = (latCandidate, lngCandidate) => {
  const lat = toNumber(latCandidate);
  const lng = toNumber(lngCandidate);
  if (lat === null || lng === null) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
};

const parseLatLngText = value => {
  const text = toText(value);
  if (!text) return null;
  const match = text.match(/(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)/);
  if (!match) return null;
  return toLatLngPair(match[1], match[2]);
};

const isGoogleMapsHostname = hostname => {
  const host = toText(hostname).toLowerCase();
  if (!host) return false;
  if (host === 'maps.app.goo.gl' || host === 'goo.gl') return true;
  if (/(\.|^)google\.[a-z.]+$/i.test(host)) return true;
  return false;
};

const normalizeMapsQueryText = value => {
  let text = decodeURIComponentSafe(toText(value).replace(/\+/g, ' '));
  text = normalizeSpace(text);
  if (!text) return '';
  const coords = parseLatLngText(text);
  if (coords && /^[-+0-9.,\s]+$/.test(text)) return '';
  return text;
};

const extractGoogleMapsCoordinates = url => {
  if (!(url instanceof URL)) return null;

  for (const key of ['q', 'query', 'll', 'center', 'sll', 'destination', 'origin', 'daddr', 'saddr']) {
    const fromParam = parseLatLngText(url.searchParams.get(key));
    if (fromParam) return fromParam;
  }

  const compactSource = `${url.pathname}${url.search}${url.hash}`;
  const atPattern = compactSource.match(/@(-?\d{1,2}(?:\.\d+)?),(-?\d{1,3}(?:\.\d+)?)/);
  if (atPattern) {
    const fromAt = toLatLngPair(atPattern[1], atPattern[2]);
    if (fromAt) return fromAt;
  }

  const threeFourPattern = compactSource.match(/!3d(-?\d{1,2}(?:\.\d+)?)!4d(-?\d{1,3}(?:\.\d+)?)/);
  if (threeFourPattern) {
    const fromThreeFour = toLatLngPair(threeFourPattern[1], threeFourPattern[2]);
    if (fromThreeFour) return fromThreeFour;
  }

  return null;
};

const extractGoogleMapsQueryText = url => {
  if (!(url instanceof URL)) return '';
  const hostname = url.hostname.toLowerCase();
  const isShortLinkHost = hostname === 'maps.app.goo.gl' || hostname === 'goo.gl';

  const candidates = [];
  const pushCandidate = value => {
    const clean = normalizeMapsQueryText(value);
    if (!clean) return;
    if (candidates.some(entry => entry.toLowerCase() === clean.toLowerCase())) return;
    candidates.push(clean);
  };

  ['q', 'query', 'destination', 'daddr', 'origin', 'saddr', 'near'].forEach(key => {
    pushCandidate(url.searchParams.get(key));
  });

  if (!isShortLinkHost) {
    const pathSegments = url.pathname.split('/').map(segment => normalizeMapsQueryText(segment)).filter(Boolean);
    const placeIndex = pathSegments.findIndex(segment => segment.toLowerCase() === 'place');
    if (placeIndex >= 0) {
      pushCandidate(pathSegments[placeIndex + 1]);
    }

    if (!candidates.length) {
      const lastSegment = pathSegments[pathSegments.length - 1];
      if (lastSegment && !['maps', 'search', 'dir'].includes(lastSegment.toLowerCase())) {
        pushCandidate(lastSegment);
      }
    }
  }

  return candidates[0] || '';
};

const parseGoogleMapsInput = rawValue => {
  const raw = toText(rawValue);
  if (!raw) {
    return { error: 'Pega una URL de Google Maps para autocompletar la ubicación.' };
  }

  const normalizedRaw = raw.includes('://') ? raw : `https://${raw}`;
  let url;
  try {
    url = new URL(normalizedRaw);
  } catch {
    return { error: 'La URL no es válida. Copia y pega el enlace completo de Google Maps.' };
  }

  if (!isGoogleMapsHostname(url.hostname)) {
    return { error: 'La URL debe ser de Google Maps (google.com/maps o maps.app.goo.gl).' };
  }

  return {
    normalizedUrl: url.toString(),
    queryText: extractGoogleMapsQueryText(url),
    coordinates: extractGoogleMapsCoordinates(url),
    shortLink: url.hostname.toLowerCase() === 'maps.app.goo.gl'
  };
};

const computeDistanceMeters = (left, right) => {
  if (!left || !right) return Number.POSITIVE_INFINITY;
  const leftLat = toNumber(left.lat);
  const leftLng = toNumber(left.lng);
  const rightLat = toNumber(right.lat);
  const rightLng = toNumber(right.lng);
  if (leftLat === null || leftLng === null || rightLat === null || rightLng === null) {
    return Number.POSITIVE_INFINITY;
  }

  const earthRadiusMeters = 6371000;
  const dLat = ((rightLat - leftLat) * Math.PI) / 180;
  const dLng = ((rightLng - leftLng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((leftLat * Math.PI) / 180) *
      Math.cos((rightLat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusMeters * c;
};

const extractLatLng = candidate => {
  if (!candidate || typeof candidate !== 'object') return null;

  const directLat = toNumber(
    candidate.lat ?? candidate.latitude ?? candidate.y ?? candidate?.properties?.lat ?? candidate?.properties?.latitude
  );
  const directLng = toNumber(
    candidate.lng ??
      candidate.lon ??
      candidate.long ??
      candidate.longitude ??
      candidate.x ??
      candidate?.properties?.lng ??
      candidate?.properties?.lon ??
      candidate?.properties?.long ??
      candidate?.properties?.longitude
  );
  if (directLat !== null && directLng !== null) {
    return { lat: directLat, lng: directLng };
  }

  const geometryCoordinates = candidate?.geometry?.coordinates;
  if (Array.isArray(geometryCoordinates) && geometryCoordinates.length >= 2) {
    const lng = toNumber(geometryCoordinates[0]);
    const lat = toNumber(geometryCoordinates[1]);
    if (lat !== null && lng !== null) {
      return { lat, lng };
    }
  }

  const coordinates = candidate?.coordinates;
  if (Array.isArray(coordinates) && coordinates.length >= 2) {
    const lng = toNumber(coordinates[0]);
    const lat = toNumber(coordinates[1]);
    if (lat !== null && lng !== null) {
      return { lat, lng };
    }
  }

  return null;
};

const isLikelyMexico = candidate => {
  const countryCode = pickText(
    candidate?.country_code,
    candidate?.address?.country_code,
    candidate?.properties?.country_code,
    candidate?.properties?.countrycode
  ).toLowerCase();
  if (countryCode) {
    return countryCode === 'mx' || countryCode === 'mex';
  }

  const countryName = pickText(
    candidate?.country,
    candidate?.address?.country,
    candidate?.properties?.country
  ).toLowerCase();
  if (countryName) {
    return countryName.includes('mex');
  }

  return true;
};

const normalizeAddressSuggestion = (candidate, index) => {
  if (!candidate || typeof candidate !== 'object') return null;
  const coordinates = extractLatLng(candidate);
  if (!coordinates) return null;
  if (!isLikelyMexico(candidate)) return null;

  const properties = candidate?.properties && typeof candidate.properties === 'object' ? candidate.properties : {};
  const address = candidate?.address && typeof candidate.address === 'object' ? candidate.address : {};

  const street = pickText(address.road, properties.street, candidate.street);
  const houseNumber = pickText(address.house_number, properties.housenumber, candidate.housenumber);
  const suburb = pickText(
    address.suburb,
    address.neighbourhood,
    address.city_district,
    properties.suburb,
    properties.neighbourhood,
    properties.district
  );
  const city = pickText(
    address.city,
    address.town,
    address.village,
    address.municipality,
    address.county,
    properties.city,
    properties.locality,
    properties.county
  );
  const state = pickText(address.state, properties.state);
  const postcode = pickText(address.postcode, properties.postcode);

  const displayName = pickText(candidate.display_name, candidate.label, properties.label);
  const primary = pickText(
    joinCompact([street, houseNumber], ' '),
    pickText(properties.name, candidate.name),
    displayName.split(',')[0]
  );
  const secondary = joinCompact([suburb, city, state, postcode]);
  const fullAddress = pickText(
    displayName,
    joinCompact([joinCompact([street, houseNumber], ' '), suburb, city, state, postcode])
  );
  const suggestedName = pickText(
    joinCompact([street, houseNumber], ' '),
    joinCompact([suburb, city], ' - '),
    primary
  );

  const id = pickText(
    candidate.place_id,
    candidate.osm_id,
    properties.id,
    properties.osm_id,
    `${coordinates.lat}_${coordinates.lng}_${index}`
  );

  return {
    id,
    lat: coordinates.lat,
    lng: coordinates.lng,
    primary: primary || fullAddress || `Ubicación ${index + 1}`,
    secondary,
    fullAddress,
    suggestedName
  };
};

const dedupeSuggestions = suggestions => {
  const seen = new Set();
  const unique = [];
  suggestions.forEach(suggestion => {
    if (!suggestion) return;
    const key = `${suggestion.id}::${suggestion.lat.toFixed(6)}::${suggestion.lng.toFixed(6)}`;
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(suggestion);
  });
  return unique;
};

const parseSuggestionPayload = payload => {
  let list = [];
  if (Array.isArray(payload)) {
    list = payload;
  } else if (Array.isArray(payload?.features)) {
    list = payload.features;
  } else if (Array.isArray(payload?.results)) {
    list = payload.results;
  } else if (Array.isArray(payload?.data)) {
    list = payload.data;
  }

  return dedupeSuggestions(
    list
      .map((item, index) => normalizeAddressSuggestion(item, index))
      .filter(Boolean)
      .slice(0, SEARCH_LIMIT)
  );
};

const fetchJson = async (url, signal) => {
  const response = await fetch(url, {
    signal,
    headers: {
      Accept: 'application/json'
    }
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Geocoder error ${response.status}: ${message}`);
  }

  return response.json();
};

const buildInternalSearchUrl = query => {
  const baseUrl =
    GEO_SEARCH_ENDPOINT.startsWith('http://') || GEO_SEARCH_ENDPOINT.startsWith('https://')
      ? GEO_SEARCH_ENDPOINT
      : `${window.location.origin}${GEO_SEARCH_ENDPOINT.startsWith('/') ? '' : '/'}${GEO_SEARCH_ENDPOINT}`;
  const url = new URL(baseUrl);
  url.searchParams.set('q', query);
  if (!url.searchParams.has('limit')) url.searchParams.set('limit', String(SEARCH_LIMIT));
  if (!url.searchParams.has('lang')) url.searchParams.set('lang', 'es');
  if (!url.searchParams.has('country') && !url.searchParams.has('countrycodes')) {
    url.searchParams.set('country', 'mx');
  }
  return url.toString();
};

const buildNominatimSearchUrl = query => {
  const url = new URL('https://nominatim.openstreetmap.org/search');
  url.searchParams.set('q', query);
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('addressdetails', '1');
  url.searchParams.set('countrycodes', 'mx');
  url.searchParams.set('accept-language', 'es-MX,es');
  url.searchParams.set('limit', String(SEARCH_LIMIT));
  return url.toString();
};

const lookupAddressSuggestions = async (query, signal) => {
  const variants = buildSearchVariants(query);
  if (!variants.length || variants[0].length < SEARCH_MIN_CHARS) {
    return { suggestions: [], source: '' };
  }

  let internalError = null;
  if (GEO_SEARCH_ENDPOINT) {
    try {
      const internalSuggestions = [];
      for (const variant of variants) {
        const payload = await fetchJson(buildInternalSearchUrl(variant), signal);
        const normalized = parseSuggestionPayload(payload);
        if (normalized.length) {
          internalSuggestions.push(...normalized);
        }
        if (internalSuggestions.length >= SEARCH_LIMIT) break;
      }
      const suggestions = dedupeSuggestions(internalSuggestions).slice(0, SEARCH_LIMIT);
      if (suggestions.length || !GEO_SEARCH_ALLOW_NOMINATIM) {
        return { suggestions, source: 'interno' };
      }
    } catch (error) {
      internalError = error;
      if (!GEO_SEARCH_ALLOW_NOMINATIM) {
        throw error;
      }
    }
  }

  if (!GEO_SEARCH_ALLOW_NOMINATIM) {
    if (internalError) throw internalError;
    return { suggestions: [], source: '' };
  }

  try {
    const fallbackSuggestions = [];
    for (const variant of variants) {
      const payload = await fetchJson(buildNominatimSearchUrl(variant), signal);
      const normalized = parseSuggestionPayload(payload);
      if (normalized.length) {
        fallbackSuggestions.push(...normalized);
      }
      if (fallbackSuggestions.length >= SEARCH_LIMIT) break;
    }
    return {
      suggestions: dedupeSuggestions(fallbackSuggestions).slice(0, SEARCH_LIMIT),
      source: 'nominatim_fallback'
    };
  } catch (fallbackError) {
    if (internalError) {
      throw new Error(
        `Falló el geocoder interno y el fallback público (${internalError.message}; ${fallbackError.message})`
      );
    }
    throw fallbackError;
  }
};

function AttendanceLocationsPanel({
  enabled,
  isAdmin,
  locationsTableId,
  fieldConfig,
  defaultRadius = DEFAULT_RADIUS
}) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [editingId, setEditingId] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState('');
  const [searchSource, setSearchSource] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [mapsUrl, setMapsUrl] = useState('');
  const [mapsLoading, setMapsLoading] = useState(false);
  const [managerVisible, setManagerVisible] = useState(false);
  const [showAdvancedTools, setShowAdvancedTools] = useState(false);
  const searchRequestRef = useRef(0);
  const [form, setForm] = useState({
    name: '',
    lat: '',
    lng: '',
    radius: String(defaultRadius),
    active: true,
    notes: ''
  });

  const activeValueNormalized = useMemo(
    () => toText(fieldConfig?.activeValue).toLowerCase(),
    [fieldConfig?.activeValue]
  );
  const readByFieldId = useMemo(() => {
    const configuredFields = [
      fieldConfig?.nameField,
      fieldConfig?.latField,
      fieldConfig?.lngField,
      fieldConfig?.radiusField,
      fieldConfig?.activeField,
      fieldConfig?.notesField
    ]
      .map(value => toText(value))
      .filter(Boolean);
    return configuredFields.length > 0 && configuredFields.every(isAirtableFieldId);
  }, [
    fieldConfig?.nameField,
    fieldConfig?.latField,
    fieldConfig?.lngField,
    fieldConfig?.radiusField,
    fieldConfig?.activeField,
    fieldConfig?.notesField
  ]);
  const formDisabled = saving || !locationsTableId || !enabled;

  const readActiveValue = value => {
    if (activeValueNormalized) {
      return toText(value).toLowerCase() === activeValueNormalized;
    }
    return toBoolean(value);
  };

  const writeActiveValue = checked => {
    if (activeValueNormalized) {
      return checked ? fieldConfig.activeValue : null;
    }
    return Boolean(checked);
  };

  const resetForm = () => {
    setForm({
      name: '',
      lat: '',
      lng: '',
      radius: String(defaultRadius),
      active: true,
      notes: ''
    });
    setEditingId('');
    setSearchQuery('');
    setSearchResults([]);
    setSearchError('');
    setSearchSource('');
    setHasSearched(false);
    setMapsUrl('');
    setShowAdvancedTools(false);
  };

  const loadLocations = async () => {
    if (!enabled || !isAdmin || !locationsTableId) {
      setLocations([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const records = await fetchAllAirtableRecords({
        tableId: locationsTableId,
        maxRecords: 500,
        returnFieldsByFieldId: readByFieldId
      });
      const next = (records || []).map(record => {
        const fields = record.fields || {};
        const nameValue = getFieldValue(fields, fieldConfig.nameField, LOCATION_FIELD_FALLBACK_NAMES.name);
        const latValue = getFieldValue(fields, fieldConfig.latField, LOCATION_FIELD_FALLBACK_NAMES.lat);
        const lngValue = getFieldValue(fields, fieldConfig.lngField, LOCATION_FIELD_FALLBACK_NAMES.lng);
        const radiusValue = getFieldValue(fields, fieldConfig.radiusField, LOCATION_FIELD_FALLBACK_NAMES.radius);
        const activeValue = getFieldValue(fields, fieldConfig.activeField, LOCATION_FIELD_FALLBACK_NAMES.active);
        const notesValue = getFieldValue(fields, fieldConfig.notesField, LOCATION_FIELD_FALLBACK_NAMES.notes);
        return {
          id: record.id,
          name: toText(nameValue),
          lat: toNumber(latValue),
          lng: toNumber(lngValue),
          radius: toNumber(radiusValue) || defaultRadius,
          active: readActiveValue(activeValue),
          notes: toText(notesValue)
        };
      });
      next.sort((left, right) => left.name.localeCompare(right.name, 'es', { sensitivity: 'base' }));
      setLocations(next);
    } catch (loadError) {
      setError(loadError.message || 'No fue posible cargar ubicaciones verificadas.');
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLocations();
  }, [enabled, isAdmin, locationsTableId]);

  useEffect(() => {
    if (!enabled || !isAdmin || !locationsTableId || !showAdvancedTools) {
      setSearchResults([]);
      setSearchError('');
      setSearchSource('');
      setSearchLoading(false);
      setHasSearched(false);
      return undefined;
    }

    const query = toText(searchQuery);
    if (query.length < SEARCH_MIN_CHARS) {
      setSearchResults([]);
      setSearchError('');
      setSearchSource('');
      setSearchLoading(false);
      setHasSearched(false);
      return undefined;
    }

    const controller = new AbortController();
    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;
    setSearchLoading(true);
    setSearchError('');

    const timer = setTimeout(async () => {
      try {
        const { suggestions, source } = await lookupAddressSuggestions(query, controller.signal);
        if (searchRequestRef.current !== requestId) return;

        setSearchResults(suggestions);
        setSearchSource(source);
        setHasSearched(true);
      } catch (lookupError) {
        if (controller.signal.aborted || searchRequestRef.current !== requestId) return;
        setSearchResults([]);
        setSearchSource('');
        setHasSearched(true);
        setSearchError(
          lookupError?.message ||
            'No fue posible buscar direcciones. Intenta de nuevo o captura coordenadas manualmente.'
        );
      } finally {
        if (searchRequestRef.current === requestId) {
          setSearchLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [enabled, isAdmin, locationsTableId, searchQuery, showAdvancedTools]);

  const applySuggestionToForm = (suggestion, feedbackMessage) => {
    if (!suggestion) return;

    setForm(prev => ({
      ...prev,
      name: prev.name || suggestion.suggestedName || suggestion.primary,
      lat: toFixedCoordinate(suggestion.lat),
      lng: toFixedCoordinate(suggestion.lng),
      notes: prev.notes || suggestion.fullAddress || suggestion.secondary || ''
    }));
    setSearchQuery(suggestion.fullAddress || suggestion.primary || '');
    setSearchResults([]);
    setSearchError('');
    setSearchSource('');
    setHasSearched(false);
    setFeedback(feedbackMessage || 'Sugerencia aplicada. Revisa nombre/radio y guarda la ubicación autorizada.');
  };

  const handleSelectSuggestion = suggestion => {
    applySuggestionToForm(suggestion);
  };

  const handleToggleAdvancedTools = () => {
    const next = !showAdvancedTools;
    setShowAdvancedTools(next);
    if (!next) {
      setSearchResults([]);
      setSearchError('');
      setSearchSource('');
      setHasSearched(false);
    }
  };

  const handlePrefillFromMapsUrl = async () => {
    if (formDisabled || mapsLoading) return;

    const parsed = parseGoogleMapsInput(mapsUrl);
    if (parsed?.error) {
      setFeedback(parsed.error);
      return;
    }

    const parsedCoordinates = parsed?.coordinates || null;
    const queryText = toText(parsed?.queryText);

    setMapsUrl(parsed?.normalizedUrl || mapsUrl);
    setFeedback('');
    setSearchError('');
    setSearchSource('');
    setSearchResults([]);
    setHasSearched(false);

    let chosenSuggestion = null;
    let lookupErrorMessage = '';

    setMapsLoading(true);
    try {
      if (queryText.length >= SEARCH_MIN_CHARS) {
        const { suggestions } = await lookupAddressSuggestions(queryText);
        if (suggestions.length) {
          if (parsedCoordinates) {
            chosenSuggestion = suggestions.reduce((best, current) => {
              if (!best) return current;
              const bestDistance = computeDistanceMeters(best, parsedCoordinates);
              const currentDistance = computeDistanceMeters(current, parsedCoordinates);
              return currentDistance < bestDistance ? current : best;
            }, null);
          } else {
            chosenSuggestion = suggestions[0];
          }
        }
      }
    } catch (lookupError) {
      lookupErrorMessage = lookupError?.message || 'No fue posible completar la búsqueda de dirección.';
    } finally {
      setMapsLoading(false);
    }

    const resolvedLat = toFixedCoordinate(parsedCoordinates?.lat ?? chosenSuggestion?.lat);
    const resolvedLng = toFixedCoordinate(parsedCoordinates?.lng ?? chosenSuggestion?.lng);
    const resolvedSearchText = pickText(chosenSuggestion?.fullAddress, chosenSuggestion?.primary, queryText);

    if (!resolvedLat || !resolvedLng) {
      if (parsed?.shortLink) {
        setFeedback(
          'No se pudieron extraer coordenadas del enlace corto. Abre la ubicación en Google Maps y pega la URL completa.'
        );
        return;
      }
      if (lookupErrorMessage) {
        setFeedback(`No se pudo prellenar desde la URL: ${lookupErrorMessage}`);
        return;
      }
      setFeedback(
        'No fue posible extraer coordenadas de la URL. Intenta con la URL completa o usa la búsqueda de dirección.'
      );
      return;
    }

    setForm(prev => ({
      ...prev,
      name: pickText(prev.name, chosenSuggestion?.suggestedName, chosenSuggestion?.primary, queryText),
      lat: resolvedLat || prev.lat,
      lng: resolvedLng || prev.lng,
      notes: pickText(prev.notes, chosenSuggestion?.fullAddress, queryText, chosenSuggestion?.secondary)
    }));

    if (resolvedSearchText) {
      setSearchQuery(resolvedSearchText);
    }

    if (lookupErrorMessage) {
      setFeedback(
        `Coordenadas prellenadas desde Google Maps. No fue posible completar la dirección: ${lookupErrorMessage}`
      );
      return;
    }

    if (chosenSuggestion) {
      setFeedback('URL de Google Maps aplicada. Coordenadas y dirección sugerida cargadas.');
      return;
    }

    setFeedback('URL de Google Maps aplicada. Coordenadas prellenadas correctamente.');
  };

  const validateForm = () => {
    const name = toText(form.name);
    const lat = toNumber(form.lat);
    const lng = toNumber(form.lng);
    const radius = toNumber(form.radius);

    if (!name) return 'Captura un nombre para la ubicación.';
    if (lat === null || lng === null) {
      return 'Primero pega una URL de Google Maps o captura coordenadas en opciones avanzadas.';
    }
    if (lat < -90 || lat > 90) return 'La latitud debe estar entre -90 y 90.';
    if (lng < -180 || lng > 180) return 'La longitud debe estar entre -180 y 180.';
    if (radius === null || radius <= 0) return 'El radio debe ser mayor a 0 metros.';
    return '';
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!enabled || !isAdmin || !locationsTableId || saving) return;

    const validationError = validateForm();
    if (validationError) {
      setFeedback(validationError);
      return;
    }

    const payload = {
      [fieldConfig.nameField]: toText(form.name),
      [fieldConfig.latField]: Number(form.lat),
      [fieldConfig.lngField]: Number(form.lng),
      [fieldConfig.radiusField]: Number(form.radius),
      [fieldConfig.activeField]: writeActiveValue(form.active),
      [fieldConfig.notesField]: toText(form.notes) || null
    };

    try {
      setSaving(true);
      setFeedback('');
      if (editingId) {
        await updateAirtableRecord({
          tableId: locationsTableId,
          recordId: editingId,
          fields: payload
        });
        setFeedback('Ubicación actualizada correctamente.');
      } else {
        await createAirtableRecord({
          tableId: locationsTableId,
          fields: payload
        });
        setFeedback('Ubicación creada correctamente.');
      }
      resetForm();
      await loadLocations();
    } catch (saveError) {
      setFeedback(saveError.message || 'No fue posible guardar la ubicación.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = location => {
    if (!location) return;
    setFeedback('');
    setSearchResults([]);
    setSearchError('');
    setSearchSource('');
    setHasSearched(false);
    setSearchQuery(location.notes || location.name || '');
    setMapsUrl('');
    setShowAdvancedTools(true);
    setEditingId(location.id);
    setForm({
      name: location.name || '',
      lat: location.lat ?? '',
      lng: location.lng ?? '',
      radius: location.radius ?? defaultRadius,
      active: Boolean(location.active),
      notes: location.notes || ''
    });
  };

  const handleDelete = async location => {
    if (!location || !locationsTableId || deletingId) return;
    const confirmed = window.confirm(`¿Eliminar la ubicación "${location.name || 'sin nombre'}"?`);
    if (!confirmed) return;

    try {
      setDeletingId(location.id);
      setFeedback('');
      await deleteAirtableRecord({
        tableId: locationsTableId,
        recordId: location.id
      });
      if (editingId === location.id) {
        resetForm();
      }
      setFeedback('Ubicación eliminada correctamente.');
      await loadLocations();
    } catch (deleteError) {
      setFeedback(deleteError.message || 'No fue posible eliminar la ubicación.');
    } finally {
      setDeletingId('');
    }
  };

  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      setFeedback('Este navegador no soporta geolocalización.');
      return;
    }
    try {
      setLocating(true);
      setFeedback('');
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 12000,
          maximumAge: 0
        });
      });
      setForm(prev => ({
        ...prev,
        lat: Number(position.coords.latitude).toFixed(6),
        lng: Number(position.coords.longitude).toFixed(6)
      }));
    } catch (locationError) {
      setFeedback(
        locationError?.code === 1
          ? 'Permite acceso a tu ubicación para autocompletar coordenadas.'
          : 'No fue posible obtener tu ubicación actual.'
      );
    } finally {
      setLocating(false);
    }
  };

  if (!isAdmin) return null;

  const showSearchEmptyState = hasSearched && !searchLoading && !searchError && !searchResults.length;
  const searchHint =
    searchSource === 'interno'
      ? 'Sugerencias del geocoder interno.'
      : searchSource === 'nominatim_fallback'
        ? 'Sugerencias con fallback público (solo contingencia).'
        : '';

  return (
    <section className="locations-panel">
      <header className="locations-header">
        <div>
          <p className="eyebrow">Reportes / Admin</p>
          <h3>Ubicaciones verificadas</h3>
          <p>Configura los lugares autorizados para registrar asistencia por geolocalización.</p>
        </div>
        <div className="locations-header-actions">
          <button
            type="button"
            className="btn ghost"
            onClick={loadLocations}
            disabled={loading || !locationsTableId || !enabled}
          >
            {loading ? 'Actualizando...' : 'Recargar'}
          </button>
          <button
            type="button"
            className={`btn ghost locations-visibility-toggle ${managerVisible ? 'active' : ''}`}
            aria-expanded={managerVisible}
            aria-controls="locations-manager-content"
            onClick={() => {
              setManagerVisible(prev => {
                const next = !prev;
                if (!next) {
                  setShowAdvancedTools(false);
                  setSearchResults([]);
                  setSearchError('');
                  setSearchSource('');
                  setHasSearched(false);
                }
                return next;
              });
            }}
          >
            {managerVisible ? 'Ocultar gestor' : 'Mostrar gestor'}
          </button>
        </div>
      </header>

      {!locationsTableId && (
        <p className="helper-text warning">
          Configura `REACT_APP_AIRTABLE_ATTENDANCE_LOCATIONS_TABLE_ID` para habilitar este panel.
        </p>
      )}
      {error && <p className="helper-text error">{error}</p>}
      {feedback && <p className="helper-text">{feedback}</p>}
      {!managerVisible && (
        <p className="helper-text locations-collapsed-note">
          Gestor oculto. Usa "Mostrar gestor" para visualizar el formulario y la tabla de ubicaciones.
        </p>
      )}

      <div
        id="locations-manager-content"
        className={`locations-collapsible ${managerVisible ? 'is-open' : 'is-closed'}`}
        aria-hidden={!managerVisible}
      >
        <div className="locations-collapsible-inner">
          <form className="locations-form-grid" onSubmit={handleSubmit}>
            <div className="locations-maps-url locations-maps-url-primary">
              <div className="locations-maps-header">
                <label htmlFor="location-google-maps-url">URL de Google Maps (método principal)</label>
                <button
                  type="button"
                  className="btn ghost compact"
                  onClick={handleToggleAdvancedTools}
                  disabled={formDisabled}
                  aria-expanded={showAdvancedTools}
                >
                  {showAdvancedTools ? 'Ocultar opciones avanzadas' : 'Mostrar opciones avanzadas'}
                </button>
              </div>
              <div className="locations-maps-url-row">
                <input
                  id="location-google-maps-url"
                  type="text"
                  inputMode="url"
                  value={mapsUrl}
                  onChange={event => setMapsUrl(event.target.value)}
                  disabled={formDisabled || mapsLoading}
                  placeholder="https://www.google.com/maps/place/..."
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="btn ghost compact"
                  disabled={formDisabled || mapsLoading || !toText(mapsUrl)}
                  onClick={handlePrefillFromMapsUrl}
                >
                  {mapsLoading ? 'Analizando...' : 'Prellenar'}
                </button>
                <button
                  type="button"
                  className="btn ghost compact"
                  disabled={formDisabled || mapsLoading || !toText(mapsUrl)}
                  onClick={() => setMapsUrl('')}
                >
                  Limpiar
                </button>
              </div>
              <p className="helper-text">
                Pega una URL de Google Maps para autocompletar coordenadas y dirección. Si usas
                `maps.app.goo.gl`, abre primero el enlace para copiar la URL completa.
              </p>
              {form.lat && form.lng && (
                <p className="helper-text">Coordenadas listas: Lat {form.lat} | Lng {form.lng}</p>
              )}
            </div>
            {showAdvancedTools && (
              <div className="locations-search">
                <label htmlFor="location-search-input">Buscar dirección (México)</label>
                <div className="locations-search-row">
                  <input
                    id="location-search-input"
                    type="search"
                    value={searchQuery}
                    onChange={event => setSearchQuery(event.target.value)}
                    disabled={formDisabled}
                    placeholder="Calle, número, colonia, ciudad, estado..."
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="btn ghost compact"
                    disabled={formDisabled || !searchQuery}
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults([]);
                      setSearchError('');
                      setSearchSource('');
                      setHasSearched(false);
                    }}
                  >
                    Limpiar
                  </button>
                </div>
                <p className="helper-text">
                  Escribe al menos {SEARCH_MIN_CHARS} caracteres para ver sugerencias de dirección.
                </p>
                {searchLoading && <p className="helper-text">Buscando direcciones...</p>}
                {searchHint && !searchLoading && <p className="helper-text">{searchHint}</p>}
                {searchError && !searchLoading && <p className="helper-text warning">{searchError}</p>}
                {showSearchEmptyState && (
                  <p className="helper-text warning">
                    No encontramos coincidencias para esa búsqueda. Prueba con más detalle: calle, número,
                    colonia o municipio.
                  </p>
                )}
                {Boolean(searchResults.length) && (
                  <div className="locations-suggestions" role="listbox" aria-label="Sugerencias de direcciones">
                    {searchResults.map(result => (
                      <button
                        key={result.id}
                        type="button"
                        className="locations-suggestion"
                        onClick={() => handleSelectSuggestion(result)}
                      >
                        <span className="locations-suggestion-title">{result.primary}</span>
                        <span className="locations-suggestion-subtitle">
                          {result.secondary || result.fullAddress || 'Dirección sugerida'}
                        </span>
                        <span className="locations-suggestion-coords">
                          Lat {toFixedCoordinate(result.lat)} | Lng {toFixedCoordinate(result.lng)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <label>
              Nombre
              <input
                type="text"
                value={form.name}
                onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))}
                disabled={formDisabled}
                placeholder="Ej. Sucursal Centro"
                required
              />
            </label>
            {showAdvancedTools && (
              <label>
                Latitud
                <input
                  type="number"
                  step="0.000001"
                  value={form.lat}
                  onChange={event => setForm(prev => ({ ...prev, lat: event.target.value }))}
                  disabled={formDisabled}
                  required
                />
              </label>
            )}
            {showAdvancedTools && (
              <label>
                Longitud
                <input
                  type="number"
                  step="0.000001"
                  value={form.lng}
                  onChange={event => setForm(prev => ({ ...prev, lng: event.target.value }))}
                  disabled={formDisabled}
                  required
                />
              </label>
            )}
            <label>
              Radio (m)
              <input
                type="number"
                min="1"
                step="1"
                value={form.radius}
                onChange={event => setForm(prev => ({ ...prev, radius: event.target.value }))}
                disabled={formDisabled}
                required
              />
            </label>
            <label className="checkbox-inline">
              <input
                type="checkbox"
                checked={Boolean(form.active)}
                onChange={event => setForm(prev => ({ ...prev, active: event.target.checked }))}
                disabled={formDisabled}
              />
              <span>Activo</span>
            </label>
            <label className="locations-notes">
              Notas (opcional)
              <textarea
                value={form.notes}
                onChange={event => setForm(prev => ({ ...prev, notes: event.target.value }))}
                disabled={formDisabled}
                rows={2}
              />
            </label>
            <div className="locations-form-actions">
              {showAdvancedTools && (
                <button
                  type="button"
                  className="btn ghost"
                  onClick={handleUseCurrentLocation}
                  disabled={locating || formDisabled}
                >
                  {locating ? 'Obteniendo ubicación...' : 'Usar mi ubicación actual'}
                </button>
              )}
              <button
                type="submit"
                className="btn primary"
                disabled={formDisabled}
              >
                {saving ? 'Guardando...' : editingId ? 'Actualizar ubicación' : 'Crear ubicación'}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="btn ghost"
                  onClick={resetForm}
                  disabled={saving}
                >
                  Cancelar edición
                </button>
              )}
            </div>
          </form>

          <div className="locations-table-wrap">
            <table className="locations-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Latitud</th>
                  <th>Longitud</th>
                  <th>Radio</th>
                  <th>Activo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {!locations.length && (
                  <tr>
                    <td colSpan={6}>No hay ubicaciones registradas.</td>
                  </tr>
                )}
                {locations.map(location => (
                  <tr key={location.id}>
                    <td>{location.name || 'Sin nombre'}</td>
                    <td>{location.lat ?? '---'}</td>
                    <td>{location.lng ?? '---'}</td>
                    <td>{location.radius ?? defaultRadius} m</td>
                    <td>{location.active ? 'Sí' : 'No'}</td>
                    <td>
                      <div className="locations-actions">
                        <button
                          type="button"
                          className="btn ghost compact"
                          onClick={() => handleEdit(location)}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="btn ghost compact"
                          onClick={() => handleDelete(location)}
                          disabled={deletingId === location.id}
                        >
                          {deletingId === location.id ? 'Eliminando...' : 'Eliminar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AttendanceLocationsPanel;
